import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtTL } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

export default function CashFlow() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/cashflow", { params: { days } }).then((r) => setD(r.data)); }, [days]);
  if (!d) return <div className="text-center py-20 text-muted-foreground">Yükleniyor...</div>;

  const chart = d.daily.map((x) => ({ ...x, day: new Date(x.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" }) }));

  return (
    <div className="space-y-6" data-testid="cashflow-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Nakit Akışı</h1>
          <p className="text-sm text-muted-foreground">Anlık ve tahmini nakit durumu</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover"><SelectItem value="7">7 Gün</SelectItem><SelectItem value="30">30 Gün</SelectItem><SelectItem value="90">90 Gün</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-5"><div className="text-xs uppercase text-muted-foreground mb-2 font-semibold">Mevcut Nakit</div><div className="text-2xl font-bold num">{fmtTL(d.current)}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase text-muted-foreground mb-2 font-semibold">Beklenen Giriş</div><div className="text-2xl font-bold num text-emerald-500">+{fmtTL(d.incoming_total)}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase text-muted-foreground mb-2 font-semibold">Beklenen Çıkış</div><div className="text-2xl font-bold num text-red-500">-{fmtTL(d.outgoing_total)}</div></Card>
        <Card className="p-5 border-primary/40 bg-primary/5"><div className="text-xs uppercase text-muted-foreground mb-2 font-semibold">Tahmini Nakit</div><div className="text-2xl font-bold num text-primary">{fmtTL(d.projected)}</div></Card>
      </div>

      <Card className="p-6">
        <h3 className="font-bold mb-4">Nakit Akış Projeksiyonu</h3>
        <div className="h-72">
          <ResponsiveContainer>
            <AreaChart data={chart}>
              <defs>
                <linearGradient id="cf" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(11 71% 58%)" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(11 71% 58%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} formatter={(v) => fmtTL(v)} />
              <Legend />
              <Area type="monotone" name="Kapanış Bakiye" dataKey="closing" stroke="hsl(11 71% 58%)" fill="url(#cf)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tarih</TableHead><TableHead className="text-right">Açılış</TableHead>
            <TableHead className="text-right">Giriş</TableHead><TableHead className="text-right">Çıkış</TableHead>
            <TableHead className="text-right">Kapanış</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {chart.slice(0, 30).map((r) => (
              <TableRow key={r.date}>
                <TableCell>{r.day}</TableCell>
                <TableCell className="text-right num">{fmtTL(r.opening)}</TableCell>
                <TableCell className="text-right num text-emerald-500">{r.in > 0 ? "+" + fmtTL(r.in) : "-"}</TableCell>
                <TableCell className="text-right num text-red-500">{r.out > 0 ? "-" + fmtTL(r.out) : "-"}</TableCell>
                <TableCell className="text-right num font-semibold">{fmtTL(r.closing)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
